import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Save, Loader2, Upload, Trash2, PenLine } from "lucide-react";
import { api, authHeaders } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AdminLayout from "@/components/admin/AdminLayout";
import { Panel, SectionLabel, inputCls } from "@/components/admin/cc";

const FIELDS = [
  { k: "greeting", label: "Afsluitzin", ph: "Met vriendelijke groeten," },
  { k: "name", label: "Naam", ph: "Victor" },
  { k: "name2", label: "Tweede naam (optioneel)", ph: "Bv. Jan" },
  { k: "role", label: "Functie (optioneel)", ph: "Zaakvoerder" },
  { k: "company", label: "Bedrijfsnaam", ph: "VHM Renovation" },
  { k: "tagline", label: "Slogan", ph: "Schilder- & afwerkingswerken" },
  { k: "phone", label: "Telefoon", ph: "+32 499 91 57 86" },
  { k: "phone2", label: "Tweede telefoon (optioneel)", ph: "" },
  { k: "email", label: "E-mail", ph: "info@…" },
  { k: "website", label: "Website", ph: "https://…" },
  { k: "address", label: "Adres", ph: "Straat, postcode gemeente", wide: true },
  { k: "vat", label: "BTW-nummer", ph: "BE 0…" },
];

const LogoBox = ({ url, onUpload, onRemove, busy }) => {
  const ref = useRef(null);
  return (
    <div className="flex items-center gap-4 mb-5">
      <div className="w-24 h-16 rounded-xl border border-cc-border bg-white flex items-center justify-center overflow-hidden" data-testid="signature-logo-preview">
        {url ? <img src={url} alt="Logo" className="max-w-full max-h-full object-contain" /> : <span className="font-body text-[11px] text-stone-400">Geen logo</span>}
      </div>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="signature-logo-input" onChange={(e) => e.target.files[0] && onUpload(e.target.files[0])} />
      <button onClick={() => ref.current?.click()} disabled={busy} data-testid="signature-logo-upload" className="inline-flex items-center gap-2 rounded-xl border border-cc-border px-4 py-2 font-body text-sm font-medium text-cc-sub hover:border-cc-accent hover:text-cc-accent transition-colors disabled:opacity-60">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Logo uploaden
      </button>
      {url && (
        <button onClick={onRemove} data-testid="signature-logo-remove" className="inline-flex items-center gap-2 rounded-xl border border-cc-border px-4 py-2 font-body text-sm font-medium text-cc-hot hover:bg-cc-hot/10 transition-colors">
          <Trash2 className="w-4 h-4" /> Verwijderen
        </button>
      )}
    </div>
  );
};

export default function Signature() {
  const { logout } = useAuth();
  const [sig, setSig] = useState(null);
  const [html, setHtml] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setSig((s) => ({ ...s, [k]: v }));

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/signature", { headers: authHeaders() });
      setSig(data.signature); setHtml(data.html);
    } catch (e) { if (e?.response?.status === 401) logout(); }
  }, [logout]);

  useEffect(() => { load(); }, [load]);

  const save = async (next = sig) => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/signature", next, { headers: authHeaders() });
      setSig(data.signature); setHtml(data.html);
      toast.success("Handtekening opgeslagen.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Opslaan mislukt.");
    } finally { setSaving(false); }
  };

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/signature/logo", fd, { headers: authHeaders() });
      await save({ ...sig, logo_url: data.logo_url });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Uploaden mislukt.");
    } finally { setUploading(false); }
  };

  if (!sig) {
    return <AdminLayout title="E-mailhandtekening"><div className="flex items-center gap-2 text-cc-faint py-10"><Loader2 className="w-5 h-5 animate-spin" /> Laden…</div></AdminLayout>;
  }

  return (
    <AdminLayout title="E-mailhandtekening" subtitle="Verschijnt automatisch onder elke e-mail die u verstuurt">
      <div className="grid lg:grid-cols-2 gap-5">
        <Panel className="p-6">
          <SectionLabel right={<PenLine className="w-4 h-4 text-cc-faint" />}>Gegevens</SectionLabel>
          <LogoBox url={sig.logo_url} busy={uploading} onUpload={upload} onRemove={() => save({ ...sig, logo_url: "" })} />
          <div className="grid sm:grid-cols-2 gap-4">
            {FIELDS.map((f) => (
              <div key={f.k} className={f.wide ? "sm:col-span-2" : ""}>
                <label className="block font-body text-sm font-medium text-cc-sub mb-2">{f.label}</label>
                <input value={sig[f.k] || ""} onChange={(e) => set(f.k, e.target.value)} placeholder={f.ph} data-testid={`signature-${f.k}`} className={inputCls} />
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 font-body text-sm text-cc-sub mt-5">
            <input type="checkbox" checked={sig.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="signature-enabled" className="w-4 h-4 accent-cc-accent" />
            Handtekening automatisch toevoegen aan elke e-mail
          </label>
          <button onClick={() => save()} disabled={saving} data-testid="signature-save" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-cc-accent text-white px-6 py-3 font-body font-semibold hover:bg-cc-accenth transition-colors disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Opslaan
          </button>
        </Panel>
        <Panel className="p-6">
          <SectionLabel>Voorbeeld (na opslaan)</SectionLabel>
          <div className="rounded-xl bg-white p-5 min-h-[160px]" data-testid="signature-preview">
            {html ? <div dangerouslySetInnerHTML={{ __html: html }} /> : <p className="font-body text-sm text-stone-400">Handtekening staat uit.</p>}
          </div>
          <p className="mt-4 font-body text-xs text-cc-faint">Geldt voor e-mails vanuit de Inbox, vanuit een lead, mailmarketing en de automatische bevestiging aan klanten.</p>
        </Panel>
      </div>
    </AdminLayout>
  );
}
